import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import DataWithLabel from '@lyra/core/components/DataWithLabel'
import Heading from '@lyra/core/components/Heading'
import Icon, { Gem, Landmark, Table, Tag } from '@lyra/core/components/Icon'
import Link from '@lyra/core/components/Link'
import Section from '@lyra/core/components/Section'
import SelectableCard from '@lyra/core/components/SelectableCard'
import NetworkIcon from '@lyra/web/components/common/NetworkIcon'
import TokenIcon from '@lyra/web/components/common/TokenIcon'
import { DepositNetwork, lyraChain } from '@lyra/web/constants/chains'
import { PageId } from '@lyra/web/constants/pages'
import { DepositTokenId, TokenId } from '@lyra/web/constants/tokens'
import { BoundWalletTransaction } from '@lyra/web/constants/transactions'
import {
  AERODROME_SWAP_URL,
  COINGECKO_DRV_URL,
  COINMARKETCAP_DRV_URL,
  GATE_DRV_URL,
  GOVERNANCE_PORTAL_URL,
  HELP_DRV_URL,
  KRAKEN_DRV_URL,
} from '@lyra/web/constants/urls'
import useAerodromeRewards from '@lyra/web/hooks/useAerodromeRewards'
import useTransaction from '@lyra/web/hooks/useTransaction'
import { formatDepositNetworkName, getChainForDepositNetwork } from '@lyra/web/utils/chains'
import getExplorerUrl from '@lyra/web/utils/getExplorerUrl'
import { getPagePath } from '@lyra/web/utils/pages'
import {
  formatTokenBalance,
  getDepositTokenAddress,
  getLyraTokenAddress,
  getNetworksForDepositToken,
} from '@lyra/web/utils/tokens'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'

import TransactionButtonRow from '../common/TransactionButtonRow'

const CARD_WIDTH = 240
const CARD_HEIGHT = 130

export default function DrvExplainerSection() {
  const { claimRewards } = useTransaction()

  const { data, isLoading, mutate } = useAerodromeRewards()
  const claimableDrvRewards = data?.claimableDrvRewards

  const handleClaimAerodromeRewards: BoundWalletTransaction = useCallback(
    async (options) => {
      if (!claimableDrvRewards) {
        throw new Error('No claimable rewards')
      }
      console.debug('claiming aerodrome rewards', claimableDrvRewards)
      await claimRewards(claimableDrvRewards.batchIds, options)
      await mutate()
    },
    [claimRewards, mutate, claimableDrvRewards]
  )

  const drvAddresses = useMemo(() => {
    const networks = getNetworksForDepositToken(DepositTokenId.DRV)
    return [
      {
        address: getLyraTokenAddress(TokenId.DRV),
        chainName: 'Derive',
        chainId: lyraChain.id,
        network: 'derive',
      },
      ...networks.map((network) => ({
        address: getDepositTokenAddress(network, DepositTokenId.DRV),
        chainName: formatDepositNetworkName(network),
        chainId: getChainForDepositNetwork(network).id,
        network,
      })),
    ]
  }, [])

  return (
    <Section isLarge>
      <Section.Header
        title="What is DRV?"
        subtitle="DRV is the native token of the Derive Protocol, utilized for governance and incentive alignment across trading and liquidity programs."
      />
      <Section.XStack isCompact style={{ overflow: 'auto' }}>
        <SelectableCard
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          gap="$1"
          onPress={() => window.open(HELP_DRV_URL, '_blank')}
        >
          <Heading size="h4" alignItems="center" display="inline-flex">
            <Icon icon={<Tag />} size={18} /> Buybacks
          </Heading>
          <BodyText color="secondary">
            25% of Derive protocol fees are allocated to DRV token buybacks. Buybacks are executed
            once a month.
          </BodyText>
        </SelectableCard>
        <SelectableCard
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          gap="$1"
          onPress={() => window.open(getPagePath({ page: PageId.Rewards }), '_blank')}
        >
          <Heading size="h4" alignItems="center" display="inline-flex">
            <Icon icon={<Gem />} size={18} /> Rewards
          </Heading>
          <BodyText color="secondary">
            Earn staked DRV when you generate protocol fees by trading or depositing collateral into
            vaults.
          </BodyText>
        </SelectableCard>
        <SelectableCard
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          gap="$1"
          onPress={() => window.open(getPagePath({ page: PageId.TradingFees }), '_blank')}
        >
          <Heading size="h4" alignItems="center" display="inline-flex">
            <Icon icon={<Table />} size={18} /> Fee Discounts
          </Heading>
          <BodyText color="secondary">
            DRV stakers get discounted fees on spot, perps and options trades based on their staked
            balance.
          </BodyText>
        </SelectableCard>
        <SelectableCard
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          gap="$1"
          onPress={() => window.open(GOVERNANCE_PORTAL_URL, '_blank')}
        >
          <Heading size="h4" alignItems="center" display="inline-flex">
            <Icon icon={<Landmark />} size={18} /> Governance
          </Heading>
          <BodyText color="secondary">
            DRV stakers get the power to delegate, vote, and make decisions to steward the future of
            the Derive ecosystem.
          </BodyText>
        </SelectableCard>
      </Section.XStack>
      <Section.Separator />
      <Section.Subheader title="Exchanges" />
      <Section.XStack isCompact>
        <Button
          size="lg"
          href={AERODROME_SWAP_URL}
          target="_blank"
          label="Aerodrome"
          leftIcon={
            <Image
              style={{ borderRadius: 1000 }}
              src="/images/aerodrome.png"
              alt="kraken"
              width={18}
              height={18}
            />
          }
        />
        <Button
          size="lg"
          href={KRAKEN_DRV_URL}
          target="_blank"
          label="Kraken"
          leftIcon={
            <Image
              style={{ borderRadius: 1000 }}
              src="/images/kraken.jpeg"
              alt="kraken"
              width={18}
              height={18}
            />
          }
        />
        <Button
          size="lg"
          href={GATE_DRV_URL}
          target="_blank"
          label="Gate.io"
          leftIcon={
            <Image
              style={{ borderRadius: 1000 }}
              src="/images/gateio.png"
              alt="Gate.io"
              width={18}
              height={18}
            />
          }
        />
        <Button
          size="lg"
          href={COINGECKO_DRV_URL}
          target="_blank"
          label="CoinGecko"
          leftIcon={
            <Image
              style={{ borderRadius: 1000 }}
              src="/images/coingecko.png"
              alt="CoinGecko"
              width={18}
              height={18}
            />
          }
        />
        <Button
          size="lg"
          href={COINMARKETCAP_DRV_URL}
          target="_blank"
          label="CoinMarketCap"
          leftIcon={
            <Image
              style={{ borderRadius: 1000 }}
              src="/images/coinmarketcap.jpeg"
              alt="CoinMarketCap"
              width={18}
              height={18}
            />
          }
        />
      </Section.XStack>
      <Section.Separator />
      <Section.Subheader title="Token Addresses" />
      <Section.XStack isCompact>
        {drvAddresses.map(({ address, chainId, chainName, network }) => (
          <Link
            key={address}
            href={getExplorerUrl(chainId, address)}
            target="_blank"
            label={
              <>
                {network === 'derive' ? (
                  <TokenIcon symbol="DRV" size={18} />
                ) : (
                  <NetworkIcon network={network as DepositNetwork} size={18} />
                )}
                &nbsp;
                {chainName}
              </>
            }
            size="lg"
          />
        ))}
      </Section.XStack>
      <Section.Separator />
      <Section.Subheader
        title="Aerodrome Rewards (Complete)"
        subtitle="LPs in the DRV-ETH pool on Aerodrome on Base from Jan 15th-29th earned DRV rewards. The program has concluded."
      />
      <Section.XStack>
        <DataWithLabel
          label="Claimable Rewards"
          value={
            isLoading ? '...' : formatTokenBalance(claimableDrvRewards?.amount ?? 0, TokenId.DRV)
          }
        />
      </Section.XStack>
      {claimableDrvRewards ? (
        <TransactionButtonRow
          label="Claim Rewards"
          reviewTitle="Claim Rewards"
          loggerAction="claim-rewards"
          onSubmitTransaction={handleClaimAerodromeRewards}
          width="fit-content"
        />
      ) : null}
    </Section>
  )
}
